import type { CountryResource } from "@/models/countryResource";
import type { LeagueAthleteFieldDefinition } from "@/models/fieldDefinition";
import type { LeagueDynamicFieldValue } from "@/models/dynamicFieldValue";
import { FieldLabel } from "./ui";

// Web port of Views/Components/LeagueDynamicFieldValueEditor.swift — renders
// an input matched to the field type and produces typed dynamic values.

function toLocalInputValue(date: Date, includeTime: boolean): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  const base = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
  if (!includeTime) return base;
  return `${base}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

export function DynamicFieldInput({
  field,
  value,
  onChange,
  countries = [],
  countryLoadError,
  onCountrySelect,
  errorMessage,
}: {
  field: LeagueAthleteFieldDefinition;
  value: LeagueDynamicFieldValue | undefined;
  onChange: (value: LeagueDynamicFieldValue | undefined) => void;
  countries?: CountryResource[];
  countryLoadError?: string | null;
  onCountrySelect?: (country: CountryResource) => void;
  errorMessage?: string;
}) {
  const label = field.isRequired ? `${field.label} *` : field.label;

  const control = (() => {
    if (field.universalAthleteRole === "country-name" && countries.length > 0) {
      return (
        <select
          value={value?.kind === "string" ? value.value : ""}
          onChange={(e) => {
            const country = countries.find((entry) => entry.officialNameEN === e.target.value);
            if (country) {
              onCountrySelect?.(country);
            } else {
              onChange(undefined);
            }
          }}
        >
          <option value="">Select country</option>
          {countries.map((country) => (
            <option key={country.officialNameEN} value={country.officialNameEN}>
              {country.officialNameEN}
            </option>
          ))}
        </select>
      );
    }

    switch (field.type) {
      case "boolean":
        return (
          <select
            value={value?.kind === "bool" ? String(value.value) : ""}
            onChange={(e) =>
              onChange(e.target.value === "" ? undefined : { kind: "bool", value: e.target.value === "true" })
            }
          >
            <option value="">—</option>
            <option value="true">Yes</option>
            <option value="false">No</option>
          </select>
        );
      case "integer":
      case "decimal":
        return (
          <input
            type="number"
            step={field.type === "integer" ? 1 : "any"}
            min={field.validation.minNumber}
            max={field.validation.maxNumber}
            value={value?.kind === "int" || value?.kind === "double" ? value.value : ""}
            onChange={(e) => {
              const raw = e.target.value;
              if (raw === "") {
                onChange(undefined);
                return;
              }
              const parsed = Number(raw);
              if (Number.isNaN(parsed)) return;
              onChange(
                field.type === "integer"
                  ? { kind: "int", value: Math.trunc(parsed) }
                  : Number.isInteger(parsed)
                    ? { kind: "int", value: parsed }
                    : { kind: "double", value: parsed },
              );
            }}
          />
        );
      case "date":
      case "dateTime": {
        const includeTime = field.type === "dateTime";
        return (
          <input
            type={includeTime ? "datetime-local" : "date"}
            value={value?.kind === "date" ? toLocalInputValue(value.value, includeTime) : ""}
            onChange={(e) => {
              const raw = e.target.value;
              if (!raw) {
                onChange(undefined);
                return;
              }
              const parsed = new Date(raw);
              if (Number.isNaN(parsed.getTime())) return;
              onChange({ kind: "date", value: parsed });
            }}
          />
        );
      }
      case "singleSelect":
        return (
          <select
            value={value?.kind === "string" ? value.value : ""}
            onChange={(e) => onChange(e.target.value ? { kind: "string", value: e.target.value } : undefined)}
          >
            <option value="">—</option>
            {field.options.map((option) => (
              <option key={option.id} value={option.id}>
                {option.label}
              </option>
            ))}
          </select>
        );
      case "multiSelect": {
        const selected = value?.kind === "stringList" ? value.value : [];
        return (
          <div className="dynamic-multiselect">
            {field.options.map((option) => (
              <label key={option.id}>
                <input
                  type="checkbox"
                  checked={selected.includes(option.id)}
                  onChange={(e) => {
                    const next = e.target.checked ? [...selected, option.id] : selected.filter((id) => id !== option.id);
                    onChange(next.length ? { kind: "stringList", value: next } : undefined);
                  }}
                />
                {option.label}
              </label>
            ))}
          </div>
        );
      }
      default:
        // text, email, url, phone, photo (photo captured as a hosted URL for now)
        return (
          <input
            type={field.type === "email" ? "email" : field.type === "url" || field.type === "photo" ? "url" : "text"}
            placeholder={field.example ?? (field.type === "photo" ? "https://…" : undefined)}
            minLength={field.validation.minLength}
            maxLength={field.validation.maxLength}
            value={value?.kind === "string" ? value.value : ""}
            onChange={(e) => onChange(e.target.value ? { kind: "string", value: e.target.value } : undefined)}
          />
        );
    }
  })();

  return (
    <FieldLabel label={label}>
      {control}
      {field.helpText && <span className="dynamic-field-help">{field.helpText}</span>}
      {field.universalAthleteRole === "country-name" && countryLoadError && (
        <span className="dynamic-field-help">Country list unavailable: {countryLoadError}</span>
      )}
      {errorMessage && <span className="sb-error-text">{errorMessage}</span>}
    </FieldLabel>
  );
}
